import { CSSProperties, FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PaginationItem, SelectChangeEvent } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

import CustomSelect from '../Dropdown';
import Text from '../Text';
import { ListPaginationProperties, TableDataProperties } from './tableProperties';
import CustomButton from '../Button';
import { PaginationStyled, PaginationWrapper, pageSizeOptions } from './tableHelpers';
import { ArrowLeft, ArrowRight } from '../../Assets/svgIcons';

const leftIcon = () => <FontAwesomeIcon icon={faArrowLeft} />;
const rightIcon = () => <FontAwesomeIcon icon={faArrowRight} />;

export const CustomPagination: FC<{
	data: TableDataProperties;
	siblingCount?: number;
	style?: CSSProperties;
	type?: string;
}> = ({ data, type = '', style, siblingCount }) => {
	const [params, setParams] = useSearchParams();

	const pageParam = type ? `${type}Page` : 'page';
	const pageSizeParam = type ? `${type}PageSize` : 'pageSize';

	const handleChangePage = (_, page: number) => {
		if (page < 1) return;
		params.set(pageParam, `${page}`);
		setParams(params, { replace: true });
	};

	const handlePageSize = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		params.set(pageParam, '1');
		params.set(pageSizeParam, value);
		setParams(params, { replace: true });
	};

	return (
		<div className="paginationWrapper" style={{ ...style }}>
			<PaginationStyled
				siblingCount={siblingCount}
				count={data.totalPages as number}
				page={parseInt(params.get(pageParam) || '1')}
				onChange={handleChangePage}
				renderItem={(item) => (
					<PaginationItem
						slots={{
							previous: leftIcon,
							next: rightIcon,
						}}
						{...item}
					/>
				)}
			/>
			<div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
				<Text title="Showing" type="psm" />
				<div style={{ width: '72px' }}>
					<CustomSelect data={pageSizeOptions} setValue={handlePageSize} value={params.get(pageSizeParam) || '8'} />
				</div>
				<div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
					<Text title="of" type="psm" />
					<Text title={data.totalNumberOfItems ?? 0} type="psm" fontWeight="semiBold" />
				</div>
			</div>
		</div>
	);
};

export const ListPagination: FC<ListPaginationProperties> = (data) => {
	const [params, setParams] = useSearchParams();
	const { hasNextPage, hasPreviousPage, loading, page, type } = data;

	const onNextClick = () => {
		if (!hasNextPage || !page) return;
		params.set('page', `${page + 1}`);
		params.set('type', type);
		setParams(params, { replace: true });
	};

	const onPrevClick = () => {
		if (!hasPreviousPage || !page) return;
		params.set('page', `${page - 1}`);
		params.set('type', type);
		setParams(params, { replace: true });
	};

	const handlePageSize = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		params.set('page', '1');
		params.set('pageSize', value);
		params.set('type', type);
		setParams(params, { replace: true });
	};

	return (
		<div className="mobilePagination">
			<div className="navigation">
				<CustomButton
					onClick={onPrevClick}
					disabled={!hasPreviousPage || loading}
					loading={loading}
					buttonType="secondaryGray"
					iconOnly={<ArrowLeft />}
				/>
				<CustomButton
					disabled={!hasNextPage || loading}
					onClick={onNextClick}
					loading={loading}
					buttonType="secondaryGray"
					iconOnly={<ArrowRight />}
				/>
			</div>
			<div style={{ width: '72px' }}>
				<CustomSelect data={pageSizeOptions} setValue={handlePageSize} value={params.get('pageSize') || '8'} />
			</div>
		</div>
	);
};

export const MobilePagination: FC<{ data: TableDataProperties }> = ({ data }) => {
	const [params, setParams] = useSearchParams();
	const { hasNextPage, hasPreviousPage, page, totalPages } = data;

	const onNextClick = () => {
		if (!hasNextPage || !page) return;
		params.set('page', (page + 1).toString());
		params.set('pageSize', '8');
		setParams(params, { replace: true });
	};

	const onPreviousClick = () => {
		if (!hasPreviousPage || !page) return;
		params.set('page', (page - 1).toString());
		params.set('pageSize', '8');
		setParams(params, { replace: true });
	};

	return (
		<PaginationWrapper>
			<CustomButton disabled={!hasPreviousPage} onClick={onPreviousClick} title="Previous" />
			<Text title={`${page} OF ${totalPages}`} type="plg" fontWeight="semiBold" />
			<CustomButton disabled={!hasNextPage} onClick={onNextClick} title="Next" />
		</PaginationWrapper>
	);
};
